// src/pages/Tools/components/ToolInfoStrip.tsx
// Replaces: .tool-info-strip, .info-strip-section, .info-strip-section.privacy

import React from 'react';
import { cn } from '../../../lib/utils';

// ─── Sub-component types ──────────────────────────────────────────────────────
interface ToolInfoStripSectionProps {
    children: React.ReactNode;
    /** privacy → green-tinted background + border */
    variant?: 'default' | 'privacy';
    className?: string;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/*
 * .info-strip-section:
 *   padding: 24px; background: white; border: 1px solid #dadce0;
 *   border-radius: 12px; text-align: left;
 *
 * .info-strip-section h3:
 *   font-size: 1.125rem; font-weight: 500; color: #202124;
 *   margin: 0 0 16px; display: flex; align-items: center; gap: 8px;
 *
 * .info-strip-section p, li:
 *   font-size: 0.9375rem; color: #5f6368; line-height: 1.6; margin: 0;
 *
 * .info-strip-section.privacy:
 *   background: #f0fdf4; border-color: #bbf7d0;
 *
 * .info-strip-section.privacy h3:
 *   color: #166534;
 */
const ToolInfoStripSection: React.FC<ToolInfoStripSectionProps> = ({
    children,
    variant = 'default',
    className,
}) => (
    <div className={cn(
        'p-[24px] border rounded-[12px] text-left',
        variant === 'privacy'
            ? 'bg-[#f0fdf4] border-[#bbf7d0] [&_h3]:text-[#166534]'
            : 'bg-white border-[#dadce0]',
        // Shared child typography via CSS child selectors
        '[&_h3]:text-[1.125rem] [&_h3]:font-medium [&_h3]:text-[#202124] [&_h3]:m-0 [&_h3]:mb-[16px] [&_h3]:flex [&_h3]:items-center [&_h3]:gap-[8px]',
        '[&_p]:text-[0.9375rem] [&_p]:text-[#5f6368] [&_p]:leading-[1.6] [&_p]:m-0',
        '[&_li]:text-[0.9375rem] [&_li]:text-[#5f6368] [&_li]:leading-[1.6] [&_li]:m-0',
        className,
    )}>
        {children}
    </div>
);

// ─── Main component type ──────────────────────────────────────────────────────
interface ToolInfoStripProps {
    children: React.ReactNode;
    className?: string;
}

type ToolInfoStripComponent = React.FC<ToolInfoStripProps> & {
    Section: React.FC<ToolInfoStripSectionProps>;
};

/*
 * .tool-info-strip:
 *   display: grid; grid-template-columns: repeat(3, 1fr);
 *   gap: 24px; margin-top: 48px; padding-top: 48px;
 *   border-top: 1px solid #dadce0;
 *   (max-width 900px: grid-template-columns: 1fr; gap: 20px)
 */
const ToolInfoStrip: ToolInfoStripComponent = ({ children, className }) => (
    <div className={cn(
        'grid grid-cols-3 gap-[24px]',
        'mt-[48px] pt-[48px]',
        'border-t border-[#dadce0]',
        'max-[900px]:grid-cols-1 max-[900px]:gap-[20px]',
        className,
    )}>
        {children}
    </div>
);

ToolInfoStrip.Section = ToolInfoStripSection;

export default ToolInfoStrip;
